import React from 'react';
import { loadCurrentState } from '../storage';
import { useCheckboxWithTimestamp } from '../hooks/useCheckboxWithTimestamp';

export const useNutritionStatus = () => {
  const saved = loadCurrentState();
  const nutrition = saved?.nutrition || {};
  const states: boolean[] = [];
  const checked = states.filter(Boolean).length;
  return { checked, total: 0 };
};

const Nutrition: React.FC = () => {
  const saved = loadCurrentState();
  const birthWeightGrams = saved?.departmentAdmission?.birthWeight ?? null;
  const weightKg = birthWeightGrams !== null && birthWeightGrams > 0 ? birthWeightGrams / 1000 : null;

  const fmt = (n: number) => parseFloat(n.toFixed(2)).toString();

  const volumeRows: { label: string; value: string; match: boolean }[] = [
    {
      label: '<1000',
      value: '80-100 מ״ל/ק״ג/יממה',
      match: birthWeightGrams !== null && birthWeightGrams < 1000,
    },
    {
      label: '1000-1500',
      value: '70-90 מ״ל/ק״ג/יממה',
      match: birthWeightGrams !== null && birthWeightGrams >= 1000 && birthWeightGrams <= 1500,
    },
    {
      label: '>1500',
      value: '60-80 מ״ל/ק״ג/יממה',
      match: birthWeightGrams !== null && birthWeightGrams > 1500,
    },
  ];

  return (
    <div className="section-content-wrapper" style={{ padding: '20px', direction: 'rtl' }}>
      <div style={{ lineHeight: '1.8', fontSize: '1rem' }}>
        <p style={{ marginBottom: '16px' }}>
          <a
            href="./preemie-nutrition"
            style={{
              color: '#103c6e',
              textDecoration: 'underline',
              fontWeight: 500
            }}
          >
            מערכת הזנת פגים
          </a>
        </p>
        <p style={{ marginBottom: '12px', fontWeight: 'bold' }}>
          הוראות להזנה על פי משקל הפג - המלצות ביממה הראשונה לחיים:
        </p>

        <ul style={{ marginBottom: '16px', paddingRight: '25px', lineHeight: '2', listStyle: 'none' }}>
          <li style={{ display: 'flex', gap: '8px' }}><span>•</span><span>ריכוז גלוקוז מומלץ 5-10% בהתאם לרמת הגלוקוז בדם</span></li>
          <li style={{ display: 'flex', gap: '8px' }}>
            <span>•</span>
            <span>
              <span dir="ltr">GIR</span> מומלץ 4-7 מ״ג/ק״ג/דקה
              {weightKg !== null && (
                <span> ({fmt(4 * weightKg)}-{fmt(7 * weightKg)} מ״ג/דקה)</span>
              )}
            </span>
          </li>
          <li style={{ display: 'flex', gap: '8px' }}>
            <span>•</span>
            <span>
              חלבון 1.5-2.5 גר׳/ק״ג/יממה
              {weightKg !== null && (
                <span> ({fmt(1.5 * weightKg)}-{fmt(2.5 * weightKg)} גרם/יממה)</span>
              )}
            </span>
          </li>
          <li style={{ display: 'flex', gap: '8px' }}>
            <span>•</span>
            <span>
              שומן 1-2 גר׳/ק״ג/יממה
              {weightKg !== null && (
                <span> ({fmt(1 * weightKg)}-{fmt(2 * weightKg)} גרם/יממה)</span>
              )}
            </span>
          </li>
          <li style={{ display: 'flex', gap: '8px' }}>
            <span>•</span>
            <span>
              קלוריות 45-55 קק״ל/ק״ג/יממה
              {weightKg !== null && (
                <span> ({fmt(45 * weightKg)}-{fmt(55 * weightKg)} קק״ל/יממה)</span>
              )}
            </span>
          </li>
          <li style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <span>•</span>
            <div>
            <span>נפח מומלץ:</span>
            <table style={{ marginTop: '8px', borderCollapse: 'collapse', fontSize: '0.9rem', minWidth: '260px' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #ccc', padding: '4px 10px', backgroundColor: '#f0f0f0', textAlign: 'right' }}>משקל (גרם)</th>
                  <th style={{ border: '1px solid #ccc', padding: '4px 10px', backgroundColor: '#f0f0f0', textAlign: 'right' }}>נפח</th>
                </tr>
              </thead>
              <tbody>
                {volumeRows.map((row) => (
                  <tr key={row.label} style={{ fontWeight: row.match ? 'bold' : 'normal' }}>
                    <td style={{ border: '1px solid #ccc', padding: '4px 10px' }}>{row.label}</td>
                    <td style={{ border: '1px solid #ccc', padding: '4px 10px' }}>{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </li>
        </ul>

        <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>
          תמיסות אפשריות ביממה ראשונה:
        </p>
        
        <ul style={{ marginBottom: '15px', paddingRight: '25px' }}>
          <li><span dir="ltr">D10P3.5WO</span></li>
          <li><span dir="ltr">D7.5P3.5WO</span></li>
          <li><span dir="ltr">D5P2.8WO</span></li>
          <li><span dir="ltr">GLU 5%</span></li>
          <li><span dir="ltr">GLU 10%</span></li>
          <li><span dir="ltr">SMOF+VITAMINS</span></li>
          <li><span dir="ltr">NACL 0.45%</span> להזלפה בקטטר עורקי</li>
        </ul>

        <p style={{ marginBottom: '10px' }}>
          בפג יציב: להתחיל מוקדם ככל הניתן הזנה אנטרלית בחלב אם או תרומה, או טיפול פה בקולוסטרום
        </p>
      </div>
    </div>
  );
};

export default Nutrition;
